import { React, useEffect, useState } from "react";
import GlobalLoadingSpinner from "../../components/GlobalLoadingSpinner";
import { Button, Tabs, Tab, Box, Typography } from "@mui/material";
import ModalPopup from "../../components/ModalPopup";
import ProductForm from "./Forms/ProductForm";
import { api } from "../../utils/api";
import ProductList from "./ProductList";
import { TextField } from "@mui/material";
import { useBasicSearch } from "../../hooks/useBasicSearch";
import { COLORS } from "../../utils/colors";
import EmptyState from "../EmptyStates/EmptyState";

const Product = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProductForm, setShowProductForm] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const { filteredData, search, setSearch } = useBasicSearch(products);

  // Filter products by category
  const subscriptions = products.filter((product) => product.type === "subscription");
  const oneTimeProducts = products.filter(
    (product) => product.type === "onceoff" && !product.isAddOnOnly
  );
  const addOnProducts = products.filter((product) => product.isAddOnOnly === true);

  // Get filtered data for current tab
  const getFilteredDataForTab = () => {
    let tabProducts = [];
    if (activeTab === 0) {
      tabProducts = subscriptions;
    } else if (activeTab === 1) {
      tabProducts = oneTimeProducts;
    } else if (activeTab === 2) {
      tabProducts = addOnProducts;
    }
    
    // Apply search filter
    if (search) {
      const searchLower = search.toLowerCase();
      return tabProducts.filter(
        (product) =>
          product.name?.toLowerCase().includes(searchLower) ||
          product.description?.toLowerCase().includes(searchLower) ||
          product.type?.toLowerCase().includes(searchLower)
      );
    }
    return tabProducts;
  };

  const filteredDataForTab = getFilteredDataForTab();

  useEffect(() => {
    fetchProducts();
  }, [loading]);

  const fetchProducts = async () => {
    try {
      // For admin portal, include all products (including isAddOnOnly)
      const isAdmin = sessionStorage.getItem("selectedPortal") == "Administrator";
      const endpoint = isAdmin ? "v1/product?includeAll=true" : "v1/product";
      const response = await api(endpoint, "GET");
      if (response.data.success) {
        setProducts(response.data.data.allProducts);
      }
    } catch (error) {
      console.error("API error:", error);
    }
    setLoading(false);
  };

  const triggerProductListUpdate = (product) => {
    console.log("New/Updated Product: ", product);
    setShowProductForm(false);
    setLoading(false);
    fetchProducts();
  };

  const AddNewProduct = () => (
    <Button
      sx={{ backgroundColor: COLORS.priorityBlue.hex }}
      variant="contained"
      onClick={() => setShowProductForm(true)}
    >
      Add New Product
    </Button>
  );

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const TabPanel = (props) => {
    const { children, value, index, ...other } = props;
    return (
      <Typography
        component="div"
        role="tabpanel"
        hidden={value !== index}
        id={`product-tabpanel-${index}`}
        aria-labelledby={`product-tab-${index}`}
        {...other}
      >
        {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
      </Typography>
    );
  };

  return (
    <div className="page product w-full flex flex-column items-center p-4">
      <div className="pageTitle mb-4 flex flex-col w-full text-center lg:flex-row lg:text-left lg:justify-between">
        <h1>Products</h1>
        <div>
          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ marginRight: "6px", width: "350px" }}
            variant="outlined"
            size="small"
            placeholder="Search for a product"
          />
          {sessionStorage.getItem("selectedPortal") == "Administrator" && (
            <>
              <AddNewProduct />
              <ModalPopup
                showModal={showProductForm}
                handleShowModal={() => setShowProductForm(false)}
                popupTitle="Add New Product"
                popupContent={<ProductForm callback={triggerProductListUpdate} />}
              />
            </>
          )}
        </div>
      </div>
      {loading ? (
        <GlobalLoadingSpinner />
      ) : (
        <div className="w-full flex flex-column">
          {sessionStorage.getItem("selectedPortal") == "Administrator" && (
            <Box sx={{ width: "100%", borderBottom: 1, borderColor: "divider", mb: 2 }}>
              <Tabs value={activeTab} onChange={handleTabChange} aria-label="product tabs">
                <Tab label={`Subscriptions (${subscriptions.length})`} />
                <Tab label={`One-Time Products (${oneTimeProducts.length})`} />
                <Tab label={`Add-On Products (${addOnProducts.length})`} />
              </Tabs>
            </Box>
          )}
          <div className="w-full flex justify-center">
            {sessionStorage.getItem("selectedPortal") == "Administrator" ? (
              <>
                <TabPanel value={activeTab} index={0}>
                  {subscriptions.length === 0 ? (
                    <EmptyState type="products" text="You have no subscription products available" action={<AddNewProduct />} />
                  ) : filteredDataForTab.length === 0 ? (
                    <EmptyState type="products" text="No subscription products match the search" />
                  ) : (
                    <ProductList products={filteredDataForTab} />
                  )}
                </TabPanel>
                <TabPanel value={activeTab} index={1}>
                  {oneTimeProducts.length === 0 ? (
                    <EmptyState type="products" text="You have no one-time products available" action={<AddNewProduct />} />
                  ) : filteredDataForTab.length === 0 ? (
                    <EmptyState type="products" text="No one-time products match the search" />
                  ) : (
                    <ProductList products={filteredDataForTab} />
                  )}
                </TabPanel>
                <TabPanel value={activeTab} index={2}>
                  {addOnProducts.length === 0 ? (
                    <EmptyState type="products" text="You have no add-on products available" action={<AddNewProduct />} />
                  ) : filteredDataForTab.length === 0 ? (
                    <EmptyState type="products" text="No add-on products match the search" />
                  ) : (
                    <ProductList products={filteredDataForTab} />
                  )}
                </TabPanel>
              </>
            ) : (
              <>
                {products.length === 0 ? (
                  <EmptyState type="products" text="You have no products available" />
                ) : filteredData.length === 0 ? (
                  <EmptyState type="products" text="No products match the search" />
                ) : (
                  <ProductList products={filteredData} />
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Product;
